import React, { useEffect, useRef, useCallback, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import '../../App.css';

interface BottomSheetProps {
  isOpen: boolean;
  onClose?: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
}

export default function BottomSheet({
  isOpen,
  onClose,
  title = 'Details',
  size = 'lg',
  children,
}: BottomSheetProps) {
  const sheetRef = useRef<HTMLElement>(null);
  const dragStartY = useRef<number | null>(null);
  const dragDelta = useRef(0);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Body scroll lock - use new class name
  useEffect(() => {
    const root = document.documentElement;
    if (isOpen) {
      root.classList.add('body--sheet-open');
    } else {
      root.classList.remove('body--sheet-open');
    }
    return () => root.classList.remove('body--sheet-open');
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Android back button / browser back closes sheet
  useEffect(() => {
    if (!isOpen) return;
    // Push a dummy history entry so pressing back pops it instead of navigating away
    // Merge with existing state to preserve React Router fields (key, idx, etc.)
    window.history.pushState({ ...(window.history.state ?? {}), __bottomSheet: true }, '');
    const handleBack = () => onCloseRef.current?.();
    window.addEventListener('popstate', handleBack);
    return () => {
      window.removeEventListener('popstate', handleBack);
      // Clean up the dummy entry if the sheet closed via overlay/ESC/swipe (not back button)
      if (window.history.state?.__bottomSheet) {
        window.history.back();
      }
    };
  }, [isOpen]);

  // Click outside to close
  const onOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).classList.contains('bottom-sheet__overlay')) {
      onClose?.();
    }
  };

  // Swipe-to-dismiss: drag handle down to close
  const onHandleTouchStart = useCallback((e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
    dragDelta.current = 0;
  }, []);

  const onHandleTouchMove = useCallback((e: React.TouchEvent) => {
    if (dragStartY.current === null) return;
    const dy = e.touches[0].clientY - dragStartY.current;
    dragDelta.current = Math.max(0, dy); // only allow dragging down
    if (sheetRef.current) {
      sheetRef.current.style.setProperty('transform', `translateY(${dragDelta.current}px)`, 'important');
      sheetRef.current.style.setProperty('transition', 'none', 'important');
    }
  }, []);

  const onHandleTouchEnd = useCallback(() => {
    if (sheetRef.current) {
      sheetRef.current.style.removeProperty('transition');
      sheetRef.current.style.removeProperty('transform');
    }
    // Dismiss if dragged more than 100px
    if (dragDelta.current > 100) {
      onClose?.();
    }
    dragStartY.current = null;
    dragDelta.current = 0;
  }, [onClose]);

  // Portal to body for proper stacking context
  return createPortal(
    <>
      <div
        className={`bottom-sheet__overlay ${isOpen ? 'open' : ''}`}
        role="button"
        aria-label="Close profile"
        onClick={onOverlayClick}
      />
      <section
        className={`bottom-sheet bottom-sheet--${size} ${isOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profileSheetTitle"
        ref={sheetRef}
      >
        <div className="bottom-sheet__panel">
          <div
            className="bottom-sheet__handle"
            onTouchStart={onHandleTouchStart}
            onTouchMove={onHandleTouchMove}
            onTouchEnd={onHandleTouchEnd}
          />
          <header className="bottom-sheet__header">
            <h3 id="profileSheetTitle">{title}</h3>
            <button className="bottom-sheet__close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </header>
          <div className="bottom-sheet__scroll">
            {children}
          </div>
        </div>
      </section>
    </>,
    document.body
  );
}
